/**
 * The Color — Theme JavaScript
 * Main JS: mobile menu, header scroll behaviour, announcement bar dismiss,
 * dropdown navigation, smooth scroll, focus management, overlay control.
 * Vanilla ES modules — no frameworks, no jQuery.
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     1. DOM Cache & Utilities
     -------------------------------------------------------------------------- */

  const selectors = {
    header: '.tc-header',
    headerSticky: '.tc-header--sticky',
    overlay: '#tc-overlay',
    mobileNav: '[data-mobile-nav]',
    mobileNavOpen: '[data-action="open-mobile-nav"]',
    mobileNavClose: '[data-action="close-mobile-nav"]',
    mobileNavToggle: '.tc-mobile-nav__toggle',
    announcementDismiss: '[data-dismiss="announcement"]',
    dropdownTrigger: '[data-dropdown] button[aria-haspopup]',
    dropdownParent: '[data-dropdown]',
  };

  /**
   * Trap focus within an element
   */
  function trapFocus(element) {
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Re-query on every Tab so late-fetched content (upsell cards, rebuilt
    // rows) is included, and filter out hidden controls (e.g. the View Cart
    // link that's display:none on mobile) so the wrap boundaries are real.
    function getFocusable() {
      return Array.from(element.querySelectorAll(focusableSelectors)).filter((el) =>
        el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null
      );
    }

    function handleTab(e) {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    element.addEventListener('keydown', handleTab);
    const first = getFocusable()[0];
    if (first) first.focus();

    return function releaseFocus() {
      element.removeEventListener('keydown', handleTab);
    };
  }

  /* --------------------------------------------------------------------------
     2. Overlay
     -------------------------------------------------------------------------- */

  const Overlay = {
    el: null,
    _savedScrollY: 0,

    init() {
      this.el = document.querySelector(selectors.overlay);
      if (!this.el) return;

      this.el.addEventListener('click', () => {
        this.hide();
        MobileNav.close();
        // Cart drawer close is handled in cart.js
        document.dispatchEvent(new CustomEvent('overlay:clicked'));
      });
    },

    show() {
      if (this.el) {
        this.el.classList.add('is-active');
        // iOS scroll-lock: save position, then fix body
        this._savedScrollY = window.scrollY;
        document.body.classList.add('drawer-open');
        document.body.style.top = `-${this._savedScrollY}px`;
      }
    },

    hide() {
      if (this.el) {
        this.el.classList.remove('is-active');
        document.body.classList.remove('drawer-open');
        document.body.style.top = '';
        // Restore scroll position
        window.scrollTo(0, this._savedScrollY);
      }
    },
  };

  /* --------------------------------------------------------------------------
     3. Mobile Navigation
     -------------------------------------------------------------------------- */

  const MobileNav = {
    drawer: null,
    openBtn: null,
    closeBtn: null,
    header: null,
    releaseFocus: null,
    previousFocus: null,

    init() {
      this.drawer = document.querySelector(selectors.mobileNav);
      this.openBtn = document.querySelector(selectors.mobileNavOpen);
      this.closeBtn = document.querySelector(selectors.mobileNavClose);
      this.header = this.openBtn ? this.openBtn.closest(selectors.header) : null;

      if (!this.drawer || !this.openBtn) return;

      this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Submenu toggles
      const toggles = this.drawer.querySelectorAll(selectors.mobileNavToggle);
      toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !isExpanded);
        });
      });

      // Close drawer when clicking on navigation links (but not toggle buttons)
      const navLinks = this.drawer.querySelectorAll('.tc-mobile-nav__link:not(.tc-mobile-nav__toggle), .tc-mobile-nav__sublink');
      navLinks.forEach((link) => {
        link.addEventListener('click', () => {
          // Delay close slightly to allow navigation to start
          setTimeout(() => this.close(), 150);
        });
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    },

    open() {
      this.previousFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      if (this.header) this.header.classList.add('is-mobile-nav-open');
      this.openBtn.setAttribute('aria-expanded', 'true');
      Overlay.show();
      this.releaseFocus = trapFocus(this.drawer);
    },

    close() {
      this.drawer.classList.remove('is-open');
      if (this.header) this.header.classList.remove('is-mobile-nav-open');
      this.openBtn.setAttribute('aria-expanded', 'false');
      Overlay.hide();
      if (this.releaseFocus) this.releaseFocus();
      if (this.previousFocus) this.previousFocus.focus();
    },
  };

  /* --------------------------------------------------------------------------
     4. Header Scroll Behaviour (Sticky / Shrink)
     -------------------------------------------------------------------------- */

  const HeaderScroll = {
    header: null,
    scrollThreshold: 50,

    init() {
      this.header = document.querySelector(selectors.header);
      if (!this.header) return;

      let ticking = false;

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.update();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this.update();
    },

    update() {
      if (!this.header) return;
      const scrolled = window.scrollY > this.scrollThreshold;
      this.header.classList.toggle('is-scrolled', scrolled);
    },
  };

  /* --------------------------------------------------------------------------
     5. Announcement Bar Dismiss
     -------------------------------------------------------------------------- */

  const AnnouncementBar = {
    init() {
      const dismissBtns = document.querySelectorAll(selectors.announcementDismiss);
      const root = document.documentElement;
      let hasVisibleBar = false;

      dismissBtns.forEach((btn) => {
        // Check if already dismissed
        const bar = btn.closest('.tc-announcement');
        if (!bar) return;

        hasVisibleBar = true;

        btn.addEventListener('click', () => {
          bar.classList.add('is-dismissed');
          // Keep sticky header flush to top once all announcement bars are dismissed.
          const hasRemainingVisible = document.querySelector('.tc-announcement:not(.is-dismissed)');
          root.style.setProperty('--announcement-offset', hasRemainingVisible ? 'var(--announcement-height)' : '0px');
        });
      });

      root.style.setProperty('--announcement-offset', hasVisibleBar ? 'var(--announcement-height)' : '0px');
    },
  };

  /* --------------------------------------------------------------------------
     6. Desktop Dropdown Navigation
     -------------------------------------------------------------------------- */

  const DropdownNav = {
    init() {
      const triggers = document.querySelectorAll(selectors.dropdownTrigger);

      triggers.forEach((trigger) => {
        const parent = trigger.closest(selectors.dropdownParent);
        if (!parent) return;

        // Click toggle for keyboard users
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isExpanded) {
            trigger.setAttribute('aria-expanded', 'true');
          }
        });

        // Keyboard: Escape closes dropdown
        parent.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
      });

      // Click outside closes all dropdowns
      document.addEventListener('click', () => {
        this.closeAll();
      });
    },

    closeAll() {
      const triggers = document.querySelectorAll(selectors.dropdownTrigger);
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    },
  };

  /* --------------------------------------------------------------------------
     7. Smooth Scroll for Anchor Links
     -------------------------------------------------------------------------- */

  const SmoothScroll = {
    init() {
      let headerHeight = document.querySelector(selectors.header)?.offsetHeight || 0;
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          headerHeight = document.querySelector(selectors.header)?.offsetHeight || 0;
        }, 200);
      });

      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener('click', (e) => {
          const targetId = anchor.getAttribute('href');
          if (targetId === '#' || targetId === '') return;

          const target = document.querySelector(targetId);
          if (!target) return;

          e.preventDefault();

          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });

          // Set focus for accessibility
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      });
    },
  };

  /* --------------------------------------------------------------------------
     8. Initialise All Modules
     -------------------------------------------------------------------------- */

  function init() {
    Overlay.init();
    MobileNav.init();
    HeaderScroll.init();
    AnnouncementBar.init();
    DropdownNav.init();
    SmoothScroll.init();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for other scripts
  window.TheColor = window.TheColor || {};
  window.TheColor.Overlay = Overlay;
  window.TheColor.MobileNav = MobileNav;
  window.TheColor.trapFocus = trapFocus;
})();
